import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { DashboardsPageFrame } from "./dashboardKit";
import { reengagementEmailReportQueryOptions } from "./reengagementEmailsReactQuery";
import {
  BLUE,
  BOOKED_HELP_TEXT,
  CLAY,
  DCMP_ENCOUNTER_HELP_TEXT,
  GOLD,
  GREEN,
  MetricCard,
  MetricCardGrid,
  MetricLabel,
  Section,
  currentMonthInTimezone,
  fmtDate,
  fmtInt,
  fmtMonth,
  fmtPct,
  fmtPointChange,
  fmtSignedInt,
  hasVisibleGroupData,
  pct,
  toNumber,
} from "./reengagementEmailsShared";
import type { ReengagementReportResponse, ReengagementSummary } from "./reengagementEmailsTypes";
import { ReengagementWaveSection } from "./reengagementEmailsWaveSection";

function MonthOverMonth({
  current,
  currentMonth,
  previousMonth,
}: {
  current: ReengagementSummary;
  currentMonth: { month: string; outcomesObservedThrough: string };
  previousMonth: ReengagementReportResponse["previousMonth"];
}) {
  const currentMonthKey = currentMonth.month;
  const previous = previousMonth.summary;
  const currentTouched = toNumber(current.uniqueCarespacesTouched);
  const previousTouched = toNumber(previous.uniqueCarespacesTouched);
  const rows = [
    {
      label: "Emails sent",
      current: fmtInt(current.emailsSent),
      previous: fmtInt(previous.emailsSent),
      change: fmtSignedInt(toNumber(current.emailsSent) - toNumber(previous.emailsSent)),
    },
    {
      label: "Unique carespaces touched",
      current: fmtInt(current.uniqueCarespacesTouched),
      previous: fmtInt(previous.uniqueCarespacesTouched),
      change: fmtSignedInt(currentTouched - previousTouched),
    },
    {
      label: "Repeated outreach rate",
      current: fmtPct(pct(current.repeatedCarespaces, currentTouched)),
      previous: fmtPct(pct(previous.repeatedCarespaces, previousTouched)),
      change: fmtPointChange(
        pct(current.repeatedCarespaces, currentTouched) -
          pct(previous.repeatedCarespaces, previousTouched),
      ),
    },
    {
      label: "Reply rate",
      current: fmtPct(pct(current.repliedCarespaces, currentTouched)),
      previous: fmtPct(pct(previous.repliedCarespaces, previousTouched)),
      change: fmtPointChange(
        pct(current.repliedCarespaces, currentTouched) -
          pct(previous.repliedCarespaces, previousTouched),
      ),
    },
    {
      label: "Booking rate",
      helpText: BOOKED_HELP_TEXT,
      current: fmtPct(pct(current.bookedCarespaces, currentTouched)),
      previous: fmtPct(pct(previous.bookedCarespaces, previousTouched)),
      change: fmtPointChange(
        pct(current.bookedCarespaces, currentTouched) -
          pct(previous.bookedCarespaces, previousTouched),
      ),
    },
    {
      label: "DCMP encounter rate",
      helpText: DCMP_ENCOUNTER_HELP_TEXT,
      current: fmtPct(pct(current.encounteredCarespaces, currentTouched)),
      previous: fmtPct(pct(previous.encounteredCarespaces, previousTouched)),
      change: fmtPointChange(
        pct(current.encounteredCarespaces, currentTouched) -
          pct(previous.encounteredCarespaces, previousTouched),
      ),
    },
  ];

  return (
    <Section
      title="Month over Month (MoM)"
      subtitle={`${fmtMonth(currentMonthKey)} compared with ${fmtMonth(previousMonth.month)}. Rate changes are shown in percentage points so different outreach volumes remain comparable.`}
    >
      {previousTouched > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-2 py-2.5">Outcome</th>
                <th className="px-2 py-2.5">{fmtMonth(currentMonthKey)}</th>
                <th className="px-2 py-2.5">{fmtMonth(previousMonth.month)}</th>
                <th className="px-2 py-2.5">Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-border/60">
                  <td className="px-2 py-3 font-semibold text-foreground">
                    <MetricLabel label={row.label} helpText={row.helpText} />
                  </td>
                  <td className="px-2 py-3">{row.current}</td>
                  <td className="px-2 py-3">{row.previous}</td>
                  <td className="px-2 py-3">{row.change}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="m-0 text-sm text-muted-foreground">
          No campaign sends were found in {fmtMonth(previousMonth.month)}, so a comparison is not
          available yet.
        </p>
      )}
      <p className="mt-3.5 mb-0 text-xs leading-relaxed text-muted-foreground">
        Outcome windows mature through the end of the following month. Current outcomes are observed
        through {fmtDate(currentMonth.outcomesObservedThrough)}; previous outcomes are observed
        through {fmtDate(previousMonth.outcomesObservedThrough)}.
      </p>
    </Section>
  );
}

export function ReengagementEmailsDashboard() {
  const [month, setMonth] = useState(() => currentMonthInTimezone("America/New_York"));
  const reportQuery = useQuery(reengagementEmailReportQueryOptions(month));
  // Clear stale report on failure (parity with standalone dashboard behavior).
  const data = reportQuery.isError ? null : (reportQuery.data ?? null);
  const loading = reportQuery.isLoading || reportQuery.isFetching;
  const error =
    reportQuery.error instanceof Error
      ? reportQuery.error.message
      : reportQuery.error
        ? "Failed to load re-engagement email data"
        : "";

  const summary = data?.summary;
  const monthlyGroups = useMemo(
    () => (data?.monthlyGroups || []).filter(hasVisibleGroupData),
    [data?.monthlyGroups],
  );
  const waves = data?.waves || [];
  const hasMonthlyUnclassified = monthlyGroups.some(
    (row) => row.outreachGroup === "unclassified" && toNumber(row.emailsSent) > 0,
  );

  return (
    <DashboardsPageFrame className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Re-engagement Emails
          </h1>
          {data ? (
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {fmtMonth(data.month)} campaign month. Waves are derived from observed campaign send
              dates, and outcomes are counted through {fmtDate(data.outcomesObservedThrough)}.
            </p>
          ) : null}
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          Campaign month
          <input
            aria-label="Campaign month"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </label>
      </div>

      {loading && !data ? (
        <div className="rounded-lg border border-border/70 bg-muted/30 px-4 py-8 text-sm text-muted-foreground">
          Loading re-engagement email report…
        </div>
      ) : null}
      {error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {data && summary && !error ? (
        <>
          <MetricCardGrid>
            <MetricCard
              label="Emails Sent"
              value={fmtInt(summary.emailsSent)}
              sub="Total campaign-tagged emails in the month"
              color={BLUE}
              face="#E9F3F8"
            />
            <MetricCard
              label="Unique Carespaces Touched"
              value={fmtInt(summary.uniqueCarespacesTouched)}
              sub="Distinct carespaces across all observed waves"
              color={BLUE}
              face="#E9F3F8"
            />
            <MetricCard
              label="Repeated Carespaces"
              value={fmtInt(summary.repeatedCarespaces)}
              sub={`${fmtPct(pct(summary.repeatedCarespaces, summary.uniqueCarespacesTouched))} of touched carespaces were emailed in more than one wave`}
              color={GREEN}
              face="#E6F5F2"
            />
            <MetricCard
              label="Changed Groups"
              value={fmtInt(summary.changedGroupCarespaces)}
              sub="Repeated carespaces that moved to a different outreach group"
              color={GOLD}
              face="#F7EED7"
            />
            <MetricCard
              label="Booked"
              helpText={BOOKED_HELP_TEXT}
              value={fmtInt(summary.bookedCarespaces)}
              sub="Distinct carespaces with a later non-cancelled booking this month window"
              color={GOLD}
              face="#F7EED7"
            />
            <MetricCard
              label="DCMP Encounter"
              helpText={DCMP_ENCOUNTER_HELP_TEXT}
              value={fmtInt(summary.encounteredCarespaces)}
              sub="Distinct carespaces with a later DCMP billing entry this month window"
              color={CLAY}
              face="#F8E1DC"
            />
          </MetricCardGrid>

          <Section
            title="Monthly Outreach Group Summary"
            subtitle="Month-aggregated across all observed waves in the selected campaign month."
          >
            {monthlyGroups.length ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="px-2 py-2.5">Outreach group</th>
                      <th className="px-2 py-2.5">Carespaces emailed</th>
                      <th className="px-2 py-2.5">Emails sent</th>
                      <th className="px-2 py-2.5">Delivered</th>
                      <th className="px-2 py-2.5">Delivery issues</th>
                      <th className="px-2 py-2.5">Replied</th>
                      <th className="px-2 py-2.5">
                        <MetricLabel label="Booked" helpText={BOOKED_HELP_TEXT} />
                      </th>
                      <th className="px-2 py-2.5">
                        <MetricLabel label="DCMP encounter" helpText={DCMP_ENCOUNTER_HELP_TEXT} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyGroups.map((row) => (
                      <tr key={row.outreachGroup} className="border-b border-border/60">
                        <td className="px-2 py-2.5 font-semibold text-foreground">{row.label}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.carespacesSent)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.emailsSent)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.deliveredCount)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.deliveryIssueCount)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.repliedCount)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.bookedCount)}</td>
                        <td className="px-2 py-2.5">{fmtInt(row.encounteredCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="m-0 text-sm text-muted-foreground">
                No group-level sends were found for the selected month.
              </p>
            )}

            {hasMonthlyUnclassified ? (
              <p className="mt-3.5 mb-0 text-xs leading-relaxed text-muted-foreground">
                This month includes campaign sends without a persisted outreach-group tag. Those
                rows appear as <strong>Unclassified Legacy Sends</strong>.
              </p>
            ) : null}
          </Section>

          <MonthOverMonth
            currentMonth={{
              month: data.month,
              outcomesObservedThrough: data.outcomesObservedThrough,
            }}
            current={summary}
            previousMonth={data.previousMonth}
          />

          <Section
            title="Monthly Interpretation"
            subtitle="The old month-level coverage view could exceed 100% because the same carespace can be re-emailed in later waves. This report now breaks the month into observed send waves and reports each wave separately."
          >
            <p className="m-0 text-sm leading-relaxed text-muted-foreground">
              For the selected month, the report found{" "}
              <strong>{fmtInt(summary.observedWaveCount)}</strong> observed wave
              {toNumber(summary.observedWaveCount) === 1 ? "" : "s"} and{" "}
              <strong>{fmtInt(summary.uniqueCarespacesTouched)}</strong> unique touched carespaces
              across <strong>{fmtInt(summary.emailsSent)}</strong> total campaign emails.
            </p>
          </Section>

          <div className="flex flex-col gap-3.5">
            {waves.map((wave) => (
              <ReengagementWaveSection
                key={wave.waveDate}
                month={data.month}
                wave={wave}
                outcomesObservedThrough={data.outcomesObservedThrough}
              />
            ))}
          </div>
        </>
      ) : null}
    </DashboardsPageFrame>
  );
}
